import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { config } from '../config';
import { keepSessionCurrent } from './session-renewal';
import { landingAfterSignIn } from './landing';

type Identity = {
  email: string;
  name: string;
  internal: boolean;
};

type AuthState = {
  identity: Identity | null;
  token: string | null;
  loading: boolean;
  login: (mode?: 'customer' | 'operator', returnTo?: string) => Promise<void>;
  register: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  completeLogin: () => Promise<string>;
};

const AuthContext = createContext<AuthState | null>(null);

const userManager = config.authMode === 'keycloak' ? new UserManager({
  authority: config.keycloakAuthority,
  client_id: config.keycloakClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Access tokens last minutes; renew them in the background with the refresh token.
  automaticSilentRenew: true
}) : null;

function identityFromUser(user: User): Identity {
  const roles = (user.profile.realm_access as { roles?: string[] } | undefined)?.roles ?? [];
  return {
    email: String(user.profile.email ?? user.profile.sub),
    name: String(user.profile.name ?? user.profile.preferred_username ?? user.profile.email ?? 'PayRight user'),
    internal: roles.includes('payright-operator')
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialMode = config.authMode === 'development' ? sessionStorage.getItem('payright-dev-role') as 'customer' | 'operator' | null : null;
  const [identity, setIdentity] = useState<Identity | null>(() => initialMode === 'operator'
    ? { email: 'operator@payright.local', name: 'Demo PayRight Staff', internal: true }
    : initialMode === 'customer' ? { email: 'demo@payright.local', name: 'Demo Customer', internal: false } : null);
  const [token, setToken] = useState<string | null>(() => initialMode ? `dev:${initialMode}` : null);
  const [loading, setLoading] = useState(config.authMode !== 'development');

  useEffect(() => {
    if (config.authMode === 'development') {
      return;
    }
    if (!userManager) return;
    const apply = (user: User | null) => {
      setToken(user?.access_token ?? null);
      setIdentity(user ? identityFromUser(user) : null);
    };
    const stop = keepSessionCurrent(userManager, apply,
      () => `${window.location.pathname}${window.location.search}${window.location.hash}`);
    void userManager.getUser().then(async (user) => {
      if (user && !user.expired) apply(user);
      // A stored session whose access token lapsed while the tab was closed can
      // still be renewed; if not, the visitor simply starts signed out.
      else if (user?.refresh_token) apply(await userManager.signinSilent().catch(() => null));
      setLoading(false);
    });
    return stop;
  }, []);

  const login = useCallback(async (mode: 'customer' | 'operator' = 'customer', returnTo = '/app') => {
    if (config.authMode === 'development') {
      sessionStorage.setItem('payright-dev-role', mode);
      setToken(`dev:${mode}`);
      setIdentity(mode === 'operator'
        ? { email: 'operator@payright.local', name: 'Demo PayRight Staff', internal: true }
        : { email: 'demo@payright.local', name: 'Demo Customer', internal: false });
      return;
    }
    await userManager?.signinRedirect({ state: { returnTo } });
  }, []);

  const register = useCallback(async (returnTo = '/app') => {
    if (config.authMode === 'development') {
      await login('customer', returnTo);
      return;
    }
    await userManager?.signinRedirect({ state: { returnTo }, extraQueryParams: { prompt: 'create' } });
  }, [login]);

  const logout = useCallback(async () => {
    if (config.authMode === 'development') {
      sessionStorage.removeItem('payright-dev-role');
      setIdentity(null);
      setToken(null);
      return;
    }
    const user = await userManager?.getUser();
    await userManager?.signoutRedirect(user?.id_token ? { id_token_hint: user.id_token } : undefined);
  }, []);

  const completeLogin = useCallback(async () => {
    if (!userManager) return '/app';
    const user = await userManager.signinRedirectCallback();
    setToken(user.access_token);
    const identity = identityFromUser(user);
    setIdentity(identity);
    return landingAfterSignIn((user.state as { returnTo?: unknown } | undefined)?.returnTo, identity.internal);
  }, []);

  const value = useMemo(() => ({ identity, token, loading, login, register, logout, completeLogin }), [identity, token, loading, login, register, logout, completeLogin]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
